/** The right-click context menu: a DOM list of actions at the cursor. */
import type { LevelTag } from "./actions.js";

export interface CtxItem {
  label: string;
  disabled?: boolean;
  run?: () => void;
  /** Action verb class for the hover highlight and menu behavior. The colour
   *  role below can specialize an interact entry without changing its kind. */
  kind?: "interact" | "walk" | "examine";
  /** A semantic menu colour. The verb's meaning supplies this, never its
   *  display label (which can change with the target's name). */
  tone?: "pickup" | "attack";
  /** A colored trailing tag (the attack verb's "(level xyz)" — danger above
   *  the hero, safe below). Rendered as a styled span, so the label itself
   *  stays plain text. */
  tag?: LevelTag;
}

/** One right-click menu: a title, its verbs, and an optional READOUT under the
 *  title (the rating plate — keyracing.md D1b). A plate is not a verb: it is
 *  never hoverable and never clickable. */
export interface CtxMenu {
  title: string;
  items: CtxItem[];
  plate?: HTMLElement;
}

export class ContextMenu {
  private el: HTMLDivElement;
  /** Wall-clock of the last open — the press that OPENS the sack menu
   *  (openSack opens on pointer-down) is the very event that arms the
   *  outside-click handler (the canvas handler runs in target phase, this
   *  one in bubble phase of the same pointerdown), so it would dismiss the
   *  menu it just opened. A short grace window makes the opening press
   *  inert. */
  private openedAt = 0;

  constructor(root: HTMLElement) {
    this.el = document.createElement("div");
    this.el.id = "ctx-menu";
    root.appendChild(this.el);
    const suppressNative = (ev: Event): void => ev.preventDefault();
    // The custom menu is still a world interaction surface: right-clicking
    // or dragging across its labels must not summon the browser menu or
    // leave a page-text selection behind.
    this.el.addEventListener("contextmenu", suppressNative);
    this.el.addEventListener("dragstart", suppressNative);
    this.el.addEventListener("selectstart", suppressNative);
    this.el.addEventListener("auxclick", suppressNative);
    this.el.addEventListener("pointerdown", (ev) => {
      if (ev.button !== 0) {
        ev.preventDefault();
        window.getSelection()?.removeAllRanges();
      }
    });
    // Pressing anywhere outside the menu (including another right click)
    // dismisses it; the document handler lets left presses pass through to
    // the game, so a left click both dismisses and acts.
    //
    // pointerdown, NOT mousedown: the canvas's own pointerdown handler
    // calls preventDefault() (the click must land the frame it is pressed),
    // and a canceled pointerdown suppresses the compatibility mousedown even
    // for a real mouse — so a document mousedown listener never sees clicks
    // on the world and the menu never closes (OSRS behaviour broken).
    document.addEventListener("pointerdown", (ev) => {
      if (this.el.contains(ev.target as Node)) return;
      if (performance.now() - this.openedAt < 100) return;
      this.close();
    });
  }

  get visible(): boolean {
    return this.el.classList.contains("show");
  }

  open(x: number, y: number, title: string, items: CtxItem[], plate?: HTMLElement): void {
    this.openedAt = performance.now();
    this.el.textContent = "";
    const t = document.createElement("div");
    t.className = "ctx-title";
    t.textContent = title;
    this.el.appendChild(t);
    // A READOUT, not a verb (keyracing.md D1b: the rating plate). It sits under
    // the title and above the actions, is never hoverable and never clickable —
    // a menu that offers "1523" as a thing you can do is a menu that lies. The
    // caller builds it, because what it shows is a display decision (see
    // ratingPlate.ts) and the menu owns layout, not meaning.
    if (plate) this.el.appendChild(plate);
    for (const item of items) {
      const b = document.createElement("div");
      b.className =
        "ctx-item" +
        (item.disabled ? " disabled" : "") +
        (item.kind ? ` kind-${item.kind}` : "") +
        (item.tone ? ` tone-${item.tone}` : "");
      b.textContent = item.label;
      if (item.tag) {
        const s = document.createElement("span");
        s.className = `ctx-tag ${item.tag.tone}`;
        s.textContent = item.tag.text;
        b.appendChild(s);
      }
      if (!item.disabled && item.run) {
        b.addEventListener("click", (ev) => {
          ev.stopPropagation();
          this.close();
          item.run!();
        });
      }
      this.el.appendChild(b);
    }
    this.el.classList.add("show");
    // Clamp to the viewport so the menu never opens off-screen.
    const rect = this.el.getBoundingClientRect();
    const px = Math.min(x, window.innerWidth - rect.width - 4);
    const py = Math.min(y, window.innerHeight - rect.height - 4);
    this.el.style.left = `${Math.max(4, px)}px`;
    this.el.style.top = `${Math.max(4, py)}px`;
  }

  close(): void {
    this.el.classList.remove("show");
  }
}
